<script lang="ts">
  /**
   * 顧客側の他社ソリューション利用 追加 / 編集 モーダル (利用ソリューション)。
   *
   * - 追加導線は `+ 利用ソリューションを追加` の一本のみ。営業担当は `競合` /
   *   `その他` を選ばず、顧客が利用している `他社ソリューション` を既存マスターから
   *   検索 + 候補選択で確定する。検索文字列だけ・候補未選択では保存できない。
   * - 自由入力からの get-or-create は廃止。保存は名前文字列ではなく
   *   `competitor_service_id` を使う。
   * - 選択後、外部ソリューション本体の `区分` を参照して保存前に
   *   「競合ソリューションとして表示されます」などを明示する。
   * - `更新拒絶期限日` / `契約終了日` は区分によらず任意。両方入力されている場合
   *   だけ、契約終了日が更新拒絶期限日以降であることを検証する。
   * - 利用状況は `利用中` / `過去利用` の2択。メモ欄は持たない。
   * - 編集モード時のみ削除ボタン。
   * - POST   /api/firms/{firmId}/competitor-services
   * - PATCH  /api/firms/{firmId}/competitor-services/{id}
   * - DELETE /api/firms/{firmId}/competitor-services/{id}
   *
   * `options` には他社ソリューションマスター候補 (id + 名前 + 区分 + 競合設定) を渡す。
   */
  import { untrack } from 'svelte';
  import {
    deriveUsageClassification,
    filterSolutionUsageOptions,
    validateSolutionUsageDraft,
  } from '@domain/competitor-service/solution-usage';
  import { customerCompetitorServicesApi } from '@shared/api/customer-competitor-services';
  import { ApiError } from '@shared/api/client';
  import {
    CUSTOMER_COMPETITOR_SWITCHABILITY_OPTIONS,
    CUSTOMER_COMPETITOR_USAGE_STATUS_OPTIONS,
    type CompetitorServiceTarget,
    type CustomerCompetitorService,
    type CustomerCompetitorServiceCreateRequest,
    type CustomerCompetitorServiceUpdateRequest,
    type CustomerCompetitorSwitchability,
  } from '@shared/types/customer-competitor-service';

  /** 保存できる利用状況 (利用中 / 過去利用) に絞った型。 */
  type SaveableUsageStatus =
    (typeof CUSTOMER_COMPETITOR_USAGE_STATUS_OPTIONS)[number]['value'];

  interface CompetitorServiceOption {
    /** competitor_services マスタの id。保存対象。 */
    id: string;
    name: string;
    kind?: 'competitor' | 'referral' | 'other' | null;
    competitorProductNames?: string[];
  }

  interface Props {
    /** 集計対象 (firm / lawyer)。 panel から渡される。 */
    target: CompetitorServiceTarget;
    options: CompetitorServiceOption[];
    initialValue: CustomerCompetitorService | null;
    canEdit: boolean;
    onClose: () => void;
    onSaved: () => void;
    onDeleted: () => void;
  }

  const {
    target,
    options,
    initialValue,
    canEdit,
    onClose,
    onSaved,
    onDeleted,
  }: Props = $props();

  const isEdit = $derived(initialValue != null);

  // 編集時は既存利用行の competitor_service_id に一致する候補を初期選択する。
  // 候補一覧に無くても id は保持して保存できるようにフォールバックを作る。
  function initialSelected(): CompetitorServiceOption | null {
    if (!initialValue) return null;
    const match = options.find(
      (o) => o.id === initialValue.competitor_service_id,
    );
    if (match) return match;
    return {
      id: initialValue.competitor_service_id,
      name: initialValue.competitor_service_name,
      kind: 'other',
      competitorProductNames: [],
    };
  }

  let selected = $state<CompetitorServiceOption | null>(
    untrack(() => initialSelected()),
  );
  let query = $state<string>('');
  let searchInput = $state<HTMLInputElement | null>(null);
  let isComboboxOpen = $state(false);
  let activeCandidateIndex = $state(-1);
  // 既存行の usage_status が considering / unknown でも利用中へ正規化して表示する
  // (新規登録 UI では 利用中 / 過去利用 のみ保存できる)。
  let usageStatus = $state<SaveableUsageStatus>(
    untrack(() => (initialValue?.usage_status === 'past' ? 'past' : 'using')),
  );
  let renewalRejectionDeadline = $state<string>(
    untrack(() => initialValue?.renewal_rejection_deadline ?? ''),
  );
  let contractEndDate = $state<string>(
    untrack(() => initialValue?.contract_end_date ?? ''),
  );
  let switchability = $state<CustomerCompetitorSwitchability | ''>(
    untrack(() => initialValue?.switchability ?? ''),
  );

  let saving = $state(false);
  let deleting = $state(false);
  let errorMessage = $state<string | null>(null);

  // 選択済み他社ソリューションの保存区分から表示分類を派生判定する。
  const derivedClassification = $derived(
    deriveUsageClassification(
      selected == null
        ? undefined
        : {
            competitor_product_names: selected.competitorProductNames,
            kind: selected.kind,
          },
    ),
  );
  const isCompetitor = $derived(derivedClassification.isCompetitor);

  const masterEmpty = $derived(options.length === 0);
  const candidates = $derived(filterSolutionUsageOptions(options, query));
  const showCandidateList = $derived(
    selected == null && isComboboxOpen && !masterEmpty && candidates.length > 0,
  );
  const showNoCandidates = $derived(
    selected == null &&
      isComboboxOpen &&
      !masterEmpty &&
      query.trim().length > 0 &&
      candidates.length === 0,
  );
  const activeCandidate = $derived(candidates[activeCandidateIndex]);
  const activeCandidateId = $derived(
    showCandidateList && activeCandidate
      ? candidateDomId(activeCandidate.id)
      : undefined,
  );

  $effect(() => {
    if (!activeCandidateId) return;
    document.getElementById(activeCandidateId)?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });
  });

  function candidateDomId(optionId: string): string {
    return `competitor-service-candidate-${optionId}`;
  }

  function selectOption(option: CompetitorServiceOption): void {
    selected = option;
    query = '';
    isComboboxOpen = false;
    activeCandidateIndex = -1;
    errorMessage = null;
  }

  function resetSelection(): void {
    selected = null;
    query = '';
    isComboboxOpen = !masterEmpty;
    activeCandidateIndex = -1;
    errorMessage = null;
    setTimeout(() => searchInput?.focus(), 0);
  }

  function openCombobox(): void {
    if (masterEmpty) return;
    isComboboxOpen = true;
    activeCandidateIndex = candidates.length > 0 ? activeCandidateIndex : -1;
  }

  function handleSearchInput(): void {
    isComboboxOpen = !masterEmpty;
    activeCandidateIndex = -1;
    errorMessage = null;
  }

  function moveActiveCandidate(direction: 1 | -1): void {
    if (candidates.length === 0) {
      activeCandidateIndex = -1;
      return;
    }
    if (activeCandidateIndex < 0) {
      activeCandidateIndex = direction === 1 ? 0 : candidates.length - 1;
      return;
    }
    activeCandidateIndex =
      (activeCandidateIndex + direction + candidates.length) %
      candidates.length;
  }

  function handleSearchKeyDown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openCombobox();
      moveActiveCandidate(1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      openCombobox();
      moveActiveCandidate(-1);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (isComboboxOpen && candidates.length > 0) {
        const option =
          candidates[activeCandidateIndex >= 0 ? activeCandidateIndex : 0];
        if (option) selectOption(option);
      }
      return;
    }
    if (event.key === 'Escape' && isComboboxOpen) {
      event.preventDefault();
      event.stopPropagation();
      isComboboxOpen = false;
      activeCandidateIndex = -1;
    }
  }

  function handleComboboxFocusOut(event: FocusEvent): void {
    const currentTarget = event.currentTarget;
    const nextTarget = event.relatedTarget;
    if (
      currentTarget instanceof HTMLElement &&
      nextTarget instanceof Node &&
      currentTarget.contains(nextTarget)
    ) {
      return;
    }
    isComboboxOpen = false;
    activeCandidateIndex = -1;
  }

  function validateLocal(): string | null {
    return validateSolutionUsageDraft({
      hasSelection: selected != null,
      renewalRejectionDeadline,
      contractEndDate,
    });
  }

  async function handleSubmit(event: Event): Promise<void> {
    event.preventDefault();
    if (!canEdit || saving) return;
    const localError = validateLocal();
    if (localError) {
      errorMessage = localError;
      return;
    }
    if (!selected) {
      errorMessage = '登録済みの他社ソリューションを選択してください。';
      return;
    }
    saving = true;
    errorMessage = null;
    try {
      if (isEdit && initialValue) {
        // PATCH は tri-state。空の日付は `null` でクリア、選択 id は常に送って
        // backend 側でマスターの存在確認と日付順の検証を再評価させる。
        const body: CustomerCompetitorServiceUpdateRequest = {
          competitor_service_id: selected.id,
          usage_status: usageStatus,
          renewal_rejection_deadline: renewalRejectionDeadline || null,
          contract_end_date: contractEndDate || null,
          switchability: switchability || null,
        };
        await customerCompetitorServicesApi.update(
          target,
          initialValue.id,
          body,
        );
      } else {
        const body: CustomerCompetitorServiceCreateRequest = {
          competitor_service_id: selected.id,
          usage_status: usageStatus,
        };
        if (renewalRejectionDeadline)
          body.renewal_rejection_deadline = renewalRejectionDeadline;
        if (contractEndDate) body.contract_end_date = contractEndDate;
        if (switchability) body.switchability = switchability;
        await customerCompetitorServicesApi.create(target, body);
      }
      onSaved();
    } catch (e) {
      errorMessage =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : '保存に失敗しました';
      saving = false;
    }
  }

  async function handleDelete(): Promise<void> {
    if (!canEdit || !isEdit || !initialValue || deleting) return;
    const confirmed = window.confirm(
      `「${initialValue.competitor_service_name}」 の利用登録を削除しますか?`,
    );
    if (!confirmed) return;
    deleting = true;
    errorMessage = null;
    try {
      await customerCompetitorServicesApi.delete(target, initialValue.id);
      onDeleted();
    } catch (e) {
      errorMessage =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : '削除に失敗しました';
      deleting = false;
    }
  }

  function handleBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) onClose();
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') onClose();
  }
</script>

<!-- backdrop は role="dialog" + aria-modal で意図した非 native の modal。
     backdrop click / Esc でのみ閉じる挙動を持たせるための listener。 -->
<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<div
  class="modal-backdrop"
  data-testid="competitor-service-modal"
  role="dialog"
  aria-modal="true"
  aria-label={isEdit ? '利用ソリューションを編集' : '利用ソリューションを追加'}
  onclick={handleBackdropClick}
  onkeydown={handleKeyDown}
  tabindex="-1"
>
  <form class="modal" onsubmit={handleSubmit}>
    <header class="modal-head">
      <h3>
        {isEdit ? '利用ソリューションを編集' : '利用ソリューションを追加'}
      </h3>
      <button
        type="button"
        class="close-btn"
        aria-label="閉じる"
        onclick={onClose}>×</button
      >
    </header>

    <div class="modal-body">
      <div class="field">
        <span class="label" id="competitor-service-search-label"
          >他社ソリューション</span
        >
        {#if selected}
          <div class="selected-row" data-testid="competitor-service-selected">
            <span
              class="selected-name"
              data-competitor-service-id={selected.id}
            >
              {selected.name}
            </span>
            <button
              type="button"
              class="link-btn"
              data-testid="competitor-service-reset"
              onclick={resetSelection}
            >
              選択し直す
            </button>
          </div>

          {#if isCompetitor}
            <p
              class="notice"
              data-testid="solution-usage-derived-notice"
              data-classification="competitor"
              data-external-solution-kind={derivedClassification.externalSolutionKind}
            >
              <strong>競合ソリューションとして表示されます。</strong>
              <span class="notice-detail">
                外部ソリューション区分: {derivedClassification.externalSolutionKindLabel}
              </span>
              <span class="notice-detail">
                対象自社ソリューション: {derivedClassification.competitorProductNames.join(
                  '、',
                )}
              </span>
            </p>
          {:else if derivedClassification.isReferral}
            <p
              class="notice is-referral"
              data-testid="solution-usage-derived-notice"
              data-classification="referral"
              data-external-solution-kind={derivedClassification.externalSolutionKind}
            >
              <strong>紹介先ソリューションとして表示されます。</strong>
              <span class="notice-detail">
                外部ソリューション区分: {derivedClassification.externalSolutionKindLabel}
              </span>
            </p>
          {:else}
            <p
              class="notice is-other"
              data-testid="solution-usage-derived-notice"
              data-classification="other"
              data-external-solution-kind={derivedClassification.externalSolutionKind}
            >
              <strong>その他ソリューションとして表示されます。</strong>
              <span class="notice-detail">
                外部ソリューション区分: {derivedClassification.externalSolutionKindLabel}
              </span>
            </p>
          {/if}
        {:else}
          <div class="combobox" onfocusout={handleComboboxFocusOut}>
            <input
              bind:this={searchInput}
              type="text"
              autocomplete="off"
              maxlength="120"
              bind:value={query}
              placeholder="検索または選択"
              role="combobox"
              aria-autocomplete="list"
              aria-haspopup="listbox"
              aria-expanded={isComboboxOpen && !masterEmpty}
              aria-controls="competitor-service-candidate-list"
              aria-activedescendant={activeCandidateId}
              data-testid="competitor-service-search"
              aria-labelledby="competitor-service-search-label"
              onfocus={openCombobox}
              onclick={openCombobox}
              oninput={handleSearchInput}
              onkeydown={handleSearchKeyDown}
            />
            {#if masterEmpty}
              <p class="hint" data-testid="competitor-service-master-empty">
                登録済みの他社ソリューションがありません。先に他社ソリューションマスターへ登録してください。
              </p>
            {:else if showNoCandidates}
              <p class="hint" data-testid="competitor-service-no-candidates">
                一致する他社ソリューションがありません。先に他社ソリューションマスターへ登録してください。
              </p>
            {:else if showCandidateList}
              <ul
                id="competitor-service-candidate-list"
                class="candidate-list"
                data-testid="competitor-service-candidates"
                role="listbox"
              >
                {#each candidates as option, index (option.id)}
                  <li>
                    <button
                      id={candidateDomId(option.id)}
                      type="button"
                      role="option"
                      tabindex="-1"
                      class:active={index === activeCandidateIndex}
                      class="candidate"
                      aria-selected={index === activeCandidateIndex}
                      data-testid="competitor-service-candidate"
                      data-competitor-service-id={option.id}
                      data-active={index === activeCandidateIndex}
                      onmousedown={(event) => event.preventDefault()}
                      onmouseenter={() => (activeCandidateIndex = index)}
                      onclick={() => selectOption(option)}
                    >
                      {option.name}
                    </button>
                  </li>
                {/each}
              </ul>
            {:else}
              <p class="hint">
                登録済みの他社ソリューションから選択してください。
              </p>
            {/if}
          </div>
        {/if}
      </div>

      {#if selected}
        <div class="field">
          <label class="label" for="competitor-service-usage-status"
            >利用状況</label
          >
          <select
            id="competitor-service-usage-status"
            bind:value={usageStatus}
            data-testid="competitor-service-usage-status"
          >
            {#each CUSTOMER_COMPETITOR_USAGE_STATUS_OPTIONS as opt (opt.value)}
              <option value={opt.value}>{opt.label}</option>
            {/each}
          </select>
        </div>

        <div class="grid-2">
          <div class="field">
            <label class="label" for="competitor-service-renewal-rejection">
              更新拒絶期限日
            </label>
            <input
              id="competitor-service-renewal-rejection"
              type="date"
              bind:value={renewalRejectionDeadline}
              data-testid="competitor-service-renewal-rejection"
            />
          </div>
          <div class="field">
            <label class="label" for="competitor-service-end-date">
              契約終了日
            </label>
            <input
              id="competitor-service-end-date"
              type="date"
              bind:value={contractEndDate}
              data-testid="competitor-service-end-date"
            />
          </div>
        </div>

        <div class="field">
          <label class="label" for="competitor-service-switchability"
            >切替余地</label
          >
          <select
            id="competitor-service-switchability"
            bind:value={switchability}
            data-testid="competitor-service-switchability"
          >
            <option value="">未設定</option>
            {#each CUSTOMER_COMPETITOR_SWITCHABILITY_OPTIONS as opt (opt.value)}
              <option value={opt.value}>{opt.label}</option>
            {/each}
          </select>
        </div>
      {/if}

      {#if errorMessage}
        <p
          class="error-msg"
          data-testid="competitor-service-error"
          role="alert"
        >
          {errorMessage}
        </p>
      {/if}
    </div>

    <footer class="modal-foot">
      {#if isEdit && canEdit}
        <button
          type="button"
          class="btn ghost danger"
          onclick={handleDelete}
          disabled={saving || deleting}
          data-testid="competitor-service-delete"
        >
          {deleting ? '削除中...' : '削除'}
        </button>
      {/if}
      <span class="spacer"></span>
      <button
        type="button"
        class="btn ghost"
        onclick={onClose}
        disabled={saving || deleting}
      >
        キャンセル
      </button>
      <button
        type="submit"
        class="btn primary"
        data-testid="competitor-service-save"
        disabled={saving || deleting || !canEdit || selected == null}
      >
        {saving ? '保存中...' : isEdit ? '保存' : '追加'}
      </button>
    </footer>
  </form>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.55);
    z-index: 1000;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 5vh 12px;
    overflow-y: auto;
    overflow-x: hidden;
    box-sizing: border-box;
  }
  .modal {
    background: #fff;
    border-radius: 10px;
    max-width: min(520px, calc(100vw - 24px));
    width: 100%;
    min-width: 0;
    box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
    display: grid;
    grid-template-rows: auto 1fr auto;
  }
  .modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #e5e7eb;
  }
  .modal-head h3 {
    margin: 0;
    font-size: 14px;
  }
  .close-btn {
    appearance: none;
    border: 0;
    background: transparent;
    color: #475569;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
  }
  .modal-body {
    padding: 14px 16px;
    display: grid;
    gap: 12px;
    max-height: 60vh;
    overflow-y: auto;
  }
  .modal-foot {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid #e5e7eb;
  }
  .spacer {
    flex: 1;
  }

  .field {
    display: grid;
    gap: 4px;
    min-width: 0;
  }
  .label {
    color: #475569;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }
  .grid-2 {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 12px;
  }
  @media (max-width: 540px) {
    .grid-2 {
      grid-template-columns: 1fr;
    }
  }
  input[type='text'],
  input[type='date'],
  select {
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 6px 8px;
    font-size: 13px;
    color: #0f172a;
    background: #fff;
    width: 100%;
    box-sizing: border-box;
    font-family: inherit;
  }
  input[type='text']:focus,
  input[type='date']:focus,
  select:focus {
    outline: 2px solid #1d4ed8;
    outline-offset: 1px;
  }

  .combobox {
    min-width: 0;
  }
  .candidate-list {
    list-style: none;
    margin: 6px 0 0;
    padding: 0;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    max-height: 220px;
    overflow-y: auto;
  }
  .candidate-list li + li {
    border-top: 1px solid #f1f5f9;
  }
  .candidate {
    appearance: none;
    border: 0;
    background: #fff;
    width: 100%;
    text-align: left;
    padding: 8px 10px;
    font-size: 13px;
    color: #0f172a;
    cursor: pointer;
    word-break: break-word;
  }
  .candidate:hover {
    background: #eff6ff;
  }
  .candidate.active,
  .candidate:focus {
    background: #eff6ff;
    outline: none;
  }

  .selected-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid #bfdbfe;
    border-radius: 6px;
    background: #f8fafc;
  }
  .selected-name {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    word-break: break-word;
  }
  .link-btn {
    appearance: none;
    border: 0;
    background: transparent;
    color: #1d4ed8;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
  }
  .link-btn:hover {
    text-decoration: underline;
  }

  .hint,
  .notice {
    margin: 6px 0 0;
    font-size: 12px;
    line-height: 1.5;
  }
  .hint {
    color: #64748b;
  }
  .notice {
    padding: 8px 10px;
    border-radius: 6px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1d4ed8;
    font-weight: 700;
  }
  .notice .notice-detail {
    display: block;
    margin-top: 2px;
    font-weight: 600;
  }
  .notice.is-other {
    background: #f8fafc;
    border-color: #e2e8f0;
    color: #475569;
  }
  .notice.is-referral {
    background: #f0fdf4;
    border-color: #bbf7d0;
    color: #166534;
  }
  .error-msg {
    margin: 0;
    color: #991b1b;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 7px 9px;
    font-size: 12px;
    font-weight: 700;
  }

  .btn {
    appearance: none;
    border: 1px solid #cbd5e1;
    background: #fff;
    color: #1f2937;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }
  .btn.primary {
    background: #1d4ed8;
    border-color: #1d4ed8;
    color: #fff;
  }
  .btn.primary:disabled,
  .btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  .btn.ghost {
    border-color: transparent;
    background: transparent;
    color: #475569;
  }
  .btn.ghost:hover:not(:disabled) {
    background: #f1f5f9;
  }
  .btn.danger {
    color: #991b1b;
  }
  .btn.danger:hover:not(:disabled) {
    background: #fef2f2;
  }
</style>
